import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Copy,
  ListFilter,
  Plus,
  Settings2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { errMsg } from "@/lib/api";
import {
  useClearLookupCache,
  usePlatformLookup,
  usePlatformLookups,
  useUpsertPlatformLookup,
} from "@/lib/platformConfig";

const EMPTY_FORM = {
  key: "",
  name: "",
  description: "",
  active: true,
};

const METADATA_TYPES = ["string", "number", "boolean", "null", "json"];

const KNOWN_METADATA_KEYS_BY_LOOKUP = {
  admission_statuses: ["next_statuses", "funnel", "initial", "can_convert"],
  attendance_statuses: ["markable"],
};

const makeId = () =>
  `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;

const cloneMetadata = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return {};
  }
};

const buildValueRow = (row = {}, index = 0) => ({
  id: makeId(),
  key: typeof row.key === "string" ? row.key : "",
  label: typeof row.label === "string" ? row.label : "",
  value:
    typeof row.value === "string"
      ? row.value
      : typeof row.key === "string"
        ? row.key
        : "",
  sort_order: Number.isFinite(Number(row.sort_order))
    ? Number(row.sort_order)
    : (index + 1) * 10,
  active: row.active !== false,
  metadata: cloneMetadata(row.metadata),
  tenant_id: typeof row.tenant_id === "string" ? row.tenant_id : null,
});

const emptyValueRow = (index = 0) => buildValueRow({}, index);

const inferMetadataType = (value) => {
  if (value === null) return "null";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (typeof value === "string") return "string";
  return "json";
};

const metadataToEditorEntry = ([key, value]) => {
  const type = inferMetadataType(value);
  return {
    id: makeId(),
    key,
    type,
    text:
      type === "json"
        ? JSON.stringify(value, null, 2)
        : type === "null"
          ? ""
          : String(value ?? ""),
    boolValue: type === "boolean" ? Boolean(value) : false,
  };
};

const parseMetadataEntry = (entry) => {
  if (entry.type === "string") {
    return { ok: true, value: entry.text ?? "" };
  }
  if (entry.type === "number") {
    const num = Number(entry.text);
    if (!Number.isFinite(num)) {
      return { ok: false, error: "Number metadata must be a valid number." };
    }
    return { ok: true, value: num };
  }
  if (entry.type === "boolean") {
    return { ok: true, value: Boolean(entry.boolValue) };
  }
  if (entry.type === "null") {
    return { ok: true, value: null };
  }
  try {
    return { ok: true, value: JSON.parse(entry.text || "null") };
  } catch {
    return { ok: false, error: "JSON metadata contains invalid JSON." };
  }
};

const metadataPreview = (metadata) => {
  const keys = Object.keys(metadata || {});
  if (!keys.length) return "No metadata";
  if (keys.length <= 2) return keys.join(", ");
  return `${keys.slice(0, 2).join(", ")} +${keys.length - 2} more`;
};

export default function PlatformLookups() {
  const lookups = usePlatformLookups();
  const saveMutation = useUpsertPlatformLookup();
  const clearCacheMutation = useClearLookupCache();

  const [selectedKey, setSelectedKey] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [valuesRows, setValuesRows] = useState([emptyValueRow()]);
  const [rowErrors, setRowErrors] = useState({});

  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [metadataRowId, setMetadataRowId] = useState(null);
  const [metadataKnown, setMetadataKnown] = useState({
    next_statuses: "",
    funnel: false,
    initial: false,
    can_convert: false,
    markable: true,
  });
  const [metadataExtras, setMetadataExtras] = useState([]);
  const [metadataError, setMetadataError] = useState("");

  const details = usePlatformLookup(selectedKey, { enabled: !!selectedKey });

  const currentLookupKey = form.key.trim().toLowerCase();

  const hydrateEditorFromBundle = (data) => {
    const values = Array.isArray(data?.values) ? data.values : [];
    setForm({
      key: data?.lookup?.key || "",
      name: data?.lookup?.name || "",
      description: data?.lookup?.description || "",
      active: data?.lookup?.active !== false,
    });
    setValuesRows(
      values.length ? values.map((row, index) => buildValueRow(row, index)) : [emptyValueRow()],
    );
    setRowErrors({});
  };

  useEffect(() => {
    if (!details.data) return;
    hydrateEditorFromBundle(details.data);
  }, [details.data]);

  const rows = lookups.data || [];

  const activeDoc = useMemo(() => {
    if (!form.key) return null;
    return rows.find((doc) => doc.key === form.key) || null;
  }, [rows, form.key]);

  const onCreateNew = () => {
    setSelectedKey("");
    setForm(EMPTY_FORM);
    setValuesRows([emptyValueRow()]);
    setRowErrors({});
  };

  const onEdit = (docKey) => {
    setSelectedKey(docKey);
  };

  const updateValueRow = (rowId, patch) => {
    setValuesRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
    );
  };

  const addValueRow = () => {
    setValuesRows((prev) => [...prev, emptyValueRow(prev.length)]);
  };

  const duplicateValueRow = (rowId) => {
    setValuesRows((prev) => {
      const index = prev.findIndex((row) => row.id === rowId);
      if (index < 0) return prev;
      const source = prev[index];
      const clone = {
        ...source,
        id: makeId(),
        sort_order: Number(source.sort_order) + 1,
        metadata: cloneMetadata(source.metadata),
      };
      const next = [...prev];
      next.splice(index + 1, 0, clone);
      return next;
    });
  };

  const removeValueRow = (rowId) => {
    setValuesRows((prev) => {
      if (prev.length <= 1) return [emptyValueRow()];
      return prev.filter((row) => row.id !== rowId);
    });
  };

  const moveValueRow = (rowId, direction) => {
    setValuesRows((prev) => {
      const index = prev.findIndex((row) => row.id === rowId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  const normalizeSortOrder = () => {
    setValuesRows((prev) =>
      prev.map((row, index) => ({ ...row, sort_order: (index + 1) * 10 })),
    );
  };

  const openMetadataEditor = (rowId) => {
    const row = valuesRows.find((item) => item.id === rowId);
    if (!row) return;
    const metadata = cloneMetadata(row.metadata);
    const knownKeys = new Set(KNOWN_METADATA_KEYS_BY_LOOKUP[currentLookupKey] || []);

    setMetadataKnown({
      next_statuses: Array.isArray(metadata.next_statuses)
        ? metadata.next_statuses.join(", ")
        : "",
      funnel: metadata.funnel === true,
      initial: metadata.initial === true,
      can_convert: metadata.can_convert === true,
      markable: metadata.markable !== false,
    });
    setMetadataExtras(
      Object.entries(metadata)
        .filter(([key]) => !knownKeys.has(key))
        .map(metadataToEditorEntry),
    );
    setMetadataError("");
    setMetadataRowId(rowId);
    setMetadataDialogOpen(true);
  };

  const addMetadataExtra = () => {
    setMetadataExtras((prev) => [
      ...prev,
      { id: makeId(), key: "", type: "string", text: "", boolValue: false },
    ]);
  };

  const updateMetadataExtra = (entryId, patch) => {
    setMetadataExtras((prev) =>
      prev.map((entry) => (entry.id === entryId ? { ...entry, ...patch } : entry)),
    );
  };

  const removeMetadataExtra = (entryId) => {
    setMetadataExtras((prev) => prev.filter((entry) => entry.id !== entryId));
  };

  const saveMetadataEditor = () => {
    const nextMetadata = {};

    if (currentLookupKey === "admission_statuses") {
      nextMetadata.next_statuses = Array.from(
        new Set(
          String(metadataKnown.next_statuses || "")
            .split(",")
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean),
        ),
      );
      nextMetadata.funnel = metadataKnown.funnel === true;
      nextMetadata.initial = metadataKnown.initial === true;
      nextMetadata.can_convert = metadataKnown.can_convert === true;
    }

    if (currentLookupKey === "attendance_statuses") {
      nextMetadata.markable = metadataKnown.markable === true;
    }

    const used = new Set(Object.keys(nextMetadata));
    for (const entry of metadataExtras) {
      const key = entry.key.trim();
      const hasValue =
        entry.type === "boolean" ||
        entry.type === "null" ||
        String(entry.text || "").trim() !== "";

      if (!key && !hasValue) {
        continue;
      }
      if (!key) {
        setMetadataError("Every metadata field must have a key.");
        return;
      }
      if (used.has(key)) {
        setMetadataError(`Duplicate metadata key '${key}'.`);
        return;
      }

      const parsed = parseMetadataEntry(entry);
      if (!parsed.ok) {
        setMetadataError(parsed.error);
        return;
      }
      used.add(key);
      nextMetadata[key] = parsed.value;
    }

    setValuesRows((prev) =>
      prev.map((row) =>
        row.id === metadataRowId ? { ...row, metadata: nextMetadata } : row,
      ),
    );
    setMetadataDialogOpen(false);
    setMetadataRowId(null);
    setMetadataError("");
  };

  const onSave = (e) => {
    e.preventDefault();
    const normalized = form.key.trim().toLowerCase();
    if (!normalized) {
      toast.error("Lookup key is required");
      return;
    }
    if (!form.name.trim()) {
      toast.error("Lookup name is required");
      return;
    }

    const nextRowErrors = {};
    const seenKeys = new Set();
    const seenValues = new Set();
    const values = [];

    for (const row of valuesRows) {
      const key = row.key.trim().toLowerCase();
      const label = row.label.trim();
      const value = (row.value || row.key).trim().toLowerCase();
      const metadata = cloneMetadata(row.metadata);

      const blankRow =
        !key &&
        !label &&
        !value &&
        Object.keys(metadata).length === 0;
      if (blankRow) continue;

      const fieldErrors = {};
      const sortOrder = Number(row.sort_order);

      if (!key) fieldErrors.key = "Required";
      if (!label) fieldErrors.label = "Required";
      if (!value) fieldErrors.value = "Required";
      if (!Number.isInteger(sortOrder)) {
        fieldErrors.sort_order = "Must be an integer";
      }
      if (key && seenKeys.has(key)) {
        fieldErrors.key = "Duplicate key";
      }
      if (value && seenValues.has(value)) {
        fieldErrors.value = "Duplicate value";
      }

      if (Object.keys(fieldErrors).length) {
        nextRowErrors[row.id] = fieldErrors;
        continue;
      }

      seenKeys.add(key);
      seenValues.add(value);
      values.push({
        key,
        label,
        value,
        sort_order: sortOrder,
        active: row.active !== false,
        metadata,
        ...(row.tenant_id ? { tenant_id: row.tenant_id } : {}),
      });
    }

    setRowErrors(nextRowErrors);
    if (Object.keys(nextRowErrors).length) {
      toast.error("Please fix value table errors before saving.");
      return;
    }

    saveMutation.mutate(
      {
        key: normalized,
        payload: {
          name: form.name,
          description: form.description || null,
          active: form.active,
          values,
        },
      },
      {
        onSuccess: (saved) => {
          toast.success(`Saved ${saved.lookup.key} (v${saved.lookup.version})`);
          setSelectedKey(saved.lookup.key);
          hydrateEditorFromBundle(saved);
        },
        onError: (error) => toast.error(errMsg(error)),
      }
    );
  };

  const onClearCache = () => {
    const normalized = form.key.trim().toLowerCase();
    clearCacheMutation.mutate(
      { key: normalized || null },
      {
        onSuccess: (result) => {
          const suffix = result.scope === "key" ? ` for ${result.key}` : "";
          toast.success(
            `Cleared ${result.keys_cleared} cache entr${
              result.keys_cleared === 1 ? "y" : "ies"
            }${suffix}`
          );
        },
        onError: (error) => toast.error(errMsg(error)),
      }
    );
  };

  if (lookups.isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-6xl" data-testid="platform-lookups-page">
        <Skeleton className="h-9 w-72 mb-4" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="platform-lookups-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)] flex items-center gap-2">
          <ListFilter className="h-6 w-6 text-[var(--accent)]" /> Platform Lookups
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Manage configurable dropdown types and values for business workflows.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-5">
        <Card className="border-[var(--subtle)] xl:col-span-3">
          <CardHeader>
            <CardTitle className="font-display">Edit Lookup</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSave} className="space-y-4">
              <div>
                <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">
                  Key
                </Label>
                <Input
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value })}
                  placeholder="student_document_kinds"
                  className="mt-1"
                  data-testid="platform-lookup-key"
                />
              </div>

              <div>
                <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">
                  Name
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Student Document Kinds"
                  className="mt-1"
                  data-testid="platform-lookup-name"
                />
              </div>

              <div>
                <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">
                  Description
                </Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Allowed document kinds for students"
                  className="mt-1"
                  data-testid="platform-lookup-description"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="platform-lookup-active"
                />
                Lookup is active
              </label>

              <div>
                <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">
                  Values
                </Label>
                <div className="mt-2 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addValueRow}
                      data-testid="platform-lookup-add-value"
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> Add value
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={normalizeSortOrder}
                      data-testid="platform-lookup-normalize-order"
                    >
                      Normalize order
                    </Button>
                  </div>

                  <div className="overflow-x-auto rounded-md border border-[var(--subtle)]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[130px]">Key</TableHead>
                          <TableHead className="min-w-[160px]">Label</TableHead>
                          <TableHead className="min-w-[130px]">Value</TableHead>
                          <TableHead className="w-[90px]">Order</TableHead>
                          <TableHead className="w-[90px]">Active</TableHead>
                          <TableHead className="min-w-[220px]">Metadata</TableHead>
                          <TableHead className="w-[160px] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {valuesRows.map((valueRow, index) => {
                          const errors = rowErrors[valueRow.id] || {};
                          return (
                            <TableRow key={valueRow.id}>
                              <TableCell>
                                <Input
                                  value={valueRow.key}
                                  onChange={(e) =>
                                    updateValueRow(valueRow.id, {
                                      key: e.target.value,
                                    })
                                  }
                                  className={cn("h-9", errors.key && "border-red-500")}
                                  data-testid={`platform-lookup-value-key-${index}`}
                                />
                                {errors.key && (
                                  <p className="mt-1 text-[11px] text-red-600">{errors.key}</p>
                                )}
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={valueRow.label}
                                  onChange={(e) =>
                                    updateValueRow(valueRow.id, {
                                      label: e.target.value,
                                    })
                                  }
                                  className={cn(
                                    "h-9",
                                    errors.label && "border-red-500",
                                  )}
                                  data-testid={`platform-lookup-value-label-${index}`}
                                />
                                {errors.label && (
                                  <p className="mt-1 text-[11px] text-red-600">{errors.label}</p>
                                )}
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={valueRow.value}
                                  onChange={(e) =>
                                    updateValueRow(valueRow.id, {
                                      value: e.target.value,
                                    })
                                  }
                                  className={cn(
                                    "h-9",
                                    errors.value && "border-red-500",
                                  )}
                                  data-testid={`platform-lookup-value-value-${index}`}
                                />
                                {errors.value && (
                                  <p className="mt-1 text-[11px] text-red-600">{errors.value}</p>
                                )}
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={valueRow.sort_order}
                                  onChange={(e) =>
                                    updateValueRow(valueRow.id, {
                                      sort_order: e.target.value,
                                    })
                                  }
                                  className={cn(
                                    "h-9",
                                    errors.sort_order && "border-red-500",
                                  )}
                                  data-testid={`platform-lookup-value-order-${index}`}
                                />
                                {errors.sort_order && (
                                  <p className="mt-1 text-[11px] text-red-600">
                                    {errors.sort_order}
                                  </p>
                                )}
                              </TableCell>
                              <TableCell>
                                <label className="inline-flex items-center gap-2 text-xs">
                                  <input
                                    type="checkbox"
                                    checked={valueRow.active}
                                    onChange={(e) =>
                                      updateValueRow(valueRow.id, {
                                        active: e.target.checked,
                                      })
                                    }
                                    data-testid={`platform-lookup-value-active-${index}`}
                                  />
                                  {valueRow.active ? "Yes" : "No"}
                                </label>
                              </TableCell>
                              <TableCell>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openMetadataEditor(valueRow.id)}
                                  data-testid={`platform-lookup-value-metadata-${index}`}
                                >
                                  <Settings2 className="mr-1.5 h-3.5 w-3.5" />
                                  Edit metadata
                                </Button>
                                <p className="mt-1 text-[11px] text-[var(--text-muted)]">
                                  {metadataPreview(valueRow.metadata)}
                                </p>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="inline-flex items-center gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => moveValueRow(valueRow.id, -1)}
                                    disabled={index === 0}
                                    aria-label="Move row up"
                                  >
                                    <ArrowUp className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => moveValueRow(valueRow.id, 1)}
                                    disabled={index === valuesRows.length - 1}
                                    aria-label="Move row down"
                                  >
                                    <ArrowDown className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => duplicateValueRow(valueRow.id)}
                                    aria-label="Duplicate row"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeValueRow(valueRow.id)}
                                    aria-label="Delete row"
                                  >
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                  disabled={saveMutation.isPending}
                  data-testid="platform-lookup-save"
                >
                  {saveMutation.isPending ? "Saving..." : "Save"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClearCache}
                  disabled={clearCacheMutation.isPending}
                  data-testid="platform-lookup-cache-clear"
                >
                  {clearCacheMutation.isPending
                    ? "Clearing..."
                    : form.key.trim()
                    ? "Clear key cache"
                    : "Clear all cache"}
                </Button>
                <Button type="button" variant="ghost" onClick={onCreateNew}>
                  New lookup
                </Button>
              </div>

              {activeDoc && (
                <p className="text-xs text-[var(--text-muted)]">
                  Current version: v{activeDoc.version} - Last update: {activeDoc.updated_at}
                </p>
              )}
            </form>
          </CardContent>
        </Card>

        <Card className="border-[var(--subtle)] xl:col-span-2">
          <CardHeader>
            <CardTitle className="font-display">Lookup Types</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-[var(--text-muted)]">
                      No lookup types yet.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((doc) => (
                  <TableRow key={doc.key}>
                    <TableCell className="font-mono text-xs">{doc.key}</TableCell>
                    <TableCell>{doc.active ? "Active" : "Inactive"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(doc.key)}
                        data-testid={`platform-lookup-edit-${doc.key}`}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Dialog open={metadataDialogOpen} onOpenChange={setMetadataDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Value Metadata</DialogTitle>
            <DialogDescription>
              Configure typed metadata fields for this value row without editing raw values JSON.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {currentLookupKey === "admission_statuses" && (
              <div className="rounded-md border border-[var(--subtle)] p-3 space-y-3 bg-[var(--bg)]">
                <div>
                  <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">
                    Next statuses (comma separated values)
                  </Label>
                  <Input
                    value={metadataKnown.next_statuses || ""}
                    onChange={(e) =>
                      setMetadataKnown((prev) => ({
                        ...prev,
                        next_statuses: e.target.value,
                      }))
                    }
                    className="mt-1"
                    placeholder="applied, shortlisted"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={metadataKnown.funnel === true}
                      onChange={(e) =>
                        setMetadataKnown((prev) => ({
                          ...prev,
                          funnel: e.target.checked,
                        }))
                      }
                    />
                    Show in funnel
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={metadataKnown.initial === true}
                      onChange={(e) =>
                        setMetadataKnown((prev) => ({
                          ...prev,
                          initial: e.target.checked,
                        }))
                      }
                    />
                    Initial status
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={metadataKnown.can_convert === true}
                      onChange={(e) =>
                        setMetadataKnown((prev) => ({
                          ...prev,
                          can_convert: e.target.checked,
                        }))
                      }
                    />
                    Can convert to student
                  </label>
                </div>
              </div>
            )}

            {currentLookupKey === "attendance_statuses" && (
              <div className="rounded-md border border-[var(--subtle)] p-3 space-y-2 bg-[var(--bg)]">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={metadataKnown.markable === true}
                    onChange={(e) =>
                      setMetadataKnown((prev) => ({
                        ...prev,
                        markable: e.target.checked,
                      }))
                    }
                  />
                  Markable in teacher attendance UI
                </label>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">
                  Additional metadata fields
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addMetadataExtra}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Add field
                </Button>
              </div>

              <div className="space-y-2">
                {metadataExtras.length === 0 ? (
                  <div className="rounded-md border border-dashed border-[var(--subtle)] p-3 text-xs text-[var(--text-muted)]">
                    No additional metadata fields.
                  </div>
                ) : (
                  metadataExtras.map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded-md border border-[var(--subtle)] p-3 space-y-2"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <Input
                          value={entry.key}
                          onChange={(e) =>
                            updateMetadataExtra(entry.id, { key: e.target.value })
                          }
                          placeholder="metadata_key"
                        />
                        <Select
                          value={entry.type}
                          onValueChange={(nextType) =>
                            updateMetadataExtra(entry.id, {
                              type: nextType,
                              text:
                                nextType === "null"
                                  ? ""
                                  : entry.text,
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {METADATA_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          className="justify-start md:justify-center"
                          onClick={() => removeMetadataExtra(entry.id)}
                        >
                          <Trash2 className="mr-1.5 h-4 w-4 text-red-600" /> Remove
                        </Button>
                      </div>

                      {entry.type === "boolean" ? (
                        <label className="inline-flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={entry.boolValue === true}
                            onChange={(e) =>
                              updateMetadataExtra(entry.id, {
                                boolValue: e.target.checked,
                              })
                            }
                          />
                          true / false
                        </label>
                      ) : entry.type === "null" ? (
                        <p className="text-xs text-[var(--text-muted)]">Value will be null.</p>
                      ) : entry.type === "json" ? (
                        <Textarea
                          value={entry.text}
                          onChange={(e) =>
                            updateMetadataExtra(entry.id, { text: e.target.value })
                          }
                          className="min-h-[110px] font-mono text-xs"
                          placeholder='{"nested": true}'
                        />
                      ) : (
                        <Input
                          value={entry.text}
                          onChange={(e) =>
                            updateMetadataExtra(entry.id, { text: e.target.value })
                          }
                          placeholder={entry.type === "number" ? "123" : "value"}
                        />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {metadataError ? (
              <p className="text-sm text-red-600" role="alert">
                {metadataError}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setMetadataDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveMetadataEditor}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="platform-lookup-metadata-save"
            >
              Save metadata
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
